import * as THREE from 'three';
import { loadGlb } from '../assets/loadGlb';

export const SKY_RADIUS = 280;
export const CINDER_GLB = '/models/env/env_cinder.glb';
export const FOG_COLOR = 0xc4b896;
export const FOG_DENSITY = 0.0042;
export const SUN_COLOR = 0xffc070;
/** Peak of Mount Cinder — always inland of Crash Coast. */
export const CINDER_PEAK = { x: -80, y: 40, z: -180 };

export type SkyHandle = {
  dome: THREE.Mesh;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  cinder: THREE.Group;
};

function paintSkyCanvas(night: boolean): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const w = 512;
  const h = 256;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const grd = ctx.createLinearGradient(0, 0, 0, h);
  if (night) {
    grd.addColorStop(0, '#0c1624');
    grd.addColorStop(0.52, '#1a2838');
    grd.addColorStop(0.82, '#2a241c');
    grd.addColorStop(1, '#3a2e20');
  } else {
    grd.addColorStop(0, '#9ec8d6');
    grd.addColorStop(0.38, '#c5d4c8');
    grd.addColorStop(0.68, '#e2c49a');
    grd.addColorStop(0.88, '#f0b060');
    grd.addColorStop(1, '#d49258');
  }
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, w, h);

  if (night) {
    ctx.fillStyle = '#dce8ff';
    for (let i = 0; i < 160; i++) {
      const x = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1;
      const y = Math.abs(Math.sin(i * 78.233) * 12345.678) % 1;
      ctx.globalAlpha = 0.28 + (i % 5) * 0.1;
      ctx.fillRect(x * w, y * h * 0.58, i % 19 === 0 ? 2 : 1, i % 19 === 0 ? 2 : 1);
    }
    ctx.globalAlpha = 1;
  } else {
    const sunX = w * 0.18;
    const sunY = h * 0.62;
    const glow = ctx.createRadialGradient(sunX, sunY, 4, sunX, sunY, 78);
    glow.addColorStop(0, 'rgba(255, 248, 220, 1)');
    glow.addColorStop(0.18, 'rgba(255, 210, 120, 0.85)');
    glow.addColorStop(0.55, 'rgba(255, 160, 70, 0.28)');
    glow.addColorStop(1, 'rgba(255, 140, 60, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#fff6d4';
    ctx.beginPath();
    ctx.arc(sunX, sunY, 14, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255, 236, 210, 0.2)';
    ctx.beginPath();
    ctx.ellipse(340, 52, 88, 16, -0.08, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(210, 78, 64, 12, 0.12, 0, Math.PI * 2);
    ctx.fill();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function fallbackSkyTexture(night: boolean): THREE.DataTexture {
  const w = 4;
  const h = 8;
  const data = new Uint8Array(w * h * 4);
  const top: [number, number, number] = night ? [12, 22, 36] : [158, 200, 214];
  const bot: [number, number, number] = night ? [58, 46, 32] : [212, 146, 88];
  for (let y = 0; y < h; y++) {
    const t = y / (h - 1);
    const r = Math.round(top[0] + (bot[0] - top[0]) * t);
    const g = Math.round(top[1] + (bot[1] - top[1]) * t);
    const b = Math.round(top[2] + (bot[2] - top[2]) * t);
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

/** Canvas sky when 2D context exists; DataTexture gradient otherwise (jsdom / SSR). */
export function createSkyTexture(night = false): THREE.Texture {
  return paintSkyCanvas(night) ?? fallbackSkyTexture(night);
}

function loadRock(): THREE.Texture | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const tex = new THREE.TextureLoader().load('/tex/rock.jpg');
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(3, 4);
  return tex;
}

function addMountCinder(scene: THREE.Scene): THREE.Group {
  const group = new THREE.Group();
  group.name = 'MT-CINDER';
  group.userData.id = 'MT-CINDER';

  const rockMap = loadRock();
  const coneMat = new THREE.MeshStandardMaterial({
    color: 0x2c2824,
    map: rockMap,
    roughness: 0.97,
    metalness: 0.02,
    fog: false,
  });
  const height = 40;
  const cone = new THREE.Mesh(new THREE.ConeGeometry(22, height, 8), coneMat);
  cone.name = 'MT-CINDER-CONE';
  cone.position.set(CINDER_PEAK.x, height * 0.5, CINDER_PEAK.z);
  cone.castShadow = false;
  cone.receiveShadow = false;
  group.add(cone);

  const shoulder = new THREE.Mesh(new THREE.ConeGeometry(16, 22, 7), coneMat);
  shoulder.name = 'MT-CINDER-SHOULDER';
  shoulder.position.set(CINDER_PEAK.x + 18, 11, CINDER_PEAK.z + 8);
  group.add(shoulder);

  const n = 56;
  const positions = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const a = i / n;
    const spread = 1.2 + a * 6.5;
    positions[i * 3] = Math.sin(i * 2.15) * spread;
    positions[i * 3 + 1] = a * 30;
    positions[i * 3 + 2] = Math.cos(i * 1.67) * spread * 0.85;
  }
  const plumeGeo = new THREE.BufferGeometry();
  plumeGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const plume = new THREE.Points(
    plumeGeo,
    new THREE.PointsMaterial({
      color: 0x6e6860,
      size: 5.2,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      fog: false,
      sizeAttenuation: true,
    }),
  );
  plume.name = 'MT-CINDER-PLUME';
  plume.position.copy(CINDER_PEAK);
  plume.userData.base = positions.slice();
  group.add(plume);

  scene.add(group);
  void dressCinder(group, cone, shoulder, rockMap);
  return group;
}

/** Swap the 8-seg cones for the authored Mount Cinder GLB. Keeps primitives if the file is missing. */
async function dressCinder(
  group: THREE.Group,
  cone: THREE.Object3D,
  shoulder: THREE.Object3D,
  rockMap: THREE.Texture | null,
): Promise<void> {
  try {
    const hero = await loadGlb(CINDER_GLB);
    hero.name = 'MT-CINDER-HERO';
    hero.position.set(CINDER_PEAK.x, 0, CINDER_PEAK.z);
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = false;
      obj.receiveShadow = false;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        mat.fog = false;
        const slug = mat.name.toLowerCase();
        if (rockMap && (slug.includes('rock') || slug.includes('ash'))) {
          mat.map = rockMap;
          mat.needsUpdate = true;
        }
      }
    });
    group.add(hero);
    cone.visible = false;
    shoulder.visible = false;
  } catch {
    cone.visible = true;
    shoulder.visible = true;
  }
}

/**
 * Late-afternoon 16:10 grade: warm horizon, pale cyan zenith, golden low sun.
 * Folds atmosphere (fog + hemi + sun + Cinder) so Game.ts stays a hook.
 */
export function addSky(scene: THREE.Scene): SkyHandle {
  const dayMap = createSkyTexture(false);
  const nightMap = createSkyTexture(true);
  const geo = new THREE.SphereGeometry(SKY_RADIUS, 32, 20);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    map: dayMap,
    side: THREE.BackSide,
    fog: false,
    depthWrite: false,
  });
  mat.name = 'SKY-DOME-MAT';
  const dome = new THREE.Mesh(geo, mat);
  dome.name = 'SKY-DOME';
  dome.frustumCulled = false;
  dome.userData.dayMap = dayMap;
  dome.userData.nightMap = nightMap;
  dome.userData.night = false;
  dome.rotation.y = 0.35;
  scene.add(dome);

  scene.fog = new THREE.FogExp2(FOG_COLOR, FOG_DENSITY);
  scene.background = new THREE.Color(FOG_COLOR);

  const hemi = new THREE.HemisphereLight(0xd8e6ee, 0x7a6a50, 0.98);
  hemi.name = 'SKY-HEMI';
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(SUN_COLOR, 1.95);
  sun.name = 'SKY-SUN';
  sun.position.set(-48, 32, 22);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 2;
  sun.shadow.camera.far = 180;
  sun.shadow.camera.left = -48;
  sun.shadow.camera.right = 48;
  sun.shadow.camera.top = 40;
  sun.shadow.camera.bottom = -52;
  sun.shadow.bias = -0.0007;
  sun.shadow.normalBias = 0.06;
  sun.target.position.set(-6, 0, -18);
  scene.add(sun);
  scene.add(sun.target);

  const fill = new THREE.DirectionalLight(0xb8cce0, 0.48);
  fill.name = 'SKY-FILL';
  fill.position.set(36, 22, -18);
  fill.castShadow = false;
  scene.add(fill);

  const nightMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    map: nightMap,
    side: THREE.BackSide,
    fog: false,
    depthWrite: false,
    transparent: true,
    opacity: 0,
  });
  nightMat.name = 'SKY-DOME-NIGHT-MAT';
  const nightDome = new THREE.Mesh(geo, nightMat);
  nightDome.name = 'SKY-DOME-NIGHT';
  nightDome.frustumCulled = false;
  nightDome.rotation.y = 0.35;
  scene.add(nightDome);

  dome.userData.sun = sun;
  dome.userData.hemi = hemi;
  dome.userData.fill = fill;
  dome.userData.nightDome = nightDome;

  const cinder = addMountCinder(scene);
  return { dome, sun, hemi, cinder };
}

/** 0 = full day, 1 = full night. Dusk 17:30–19:30, dawn 05:00–06:30. */
export function nightBlend(todMinutes: number): number {
  if (!Number.isFinite(todMinutes)) {
    return 0;
  }
  const day = 24 * 60;
  const m = ((Math.floor(todMinutes) % day) + day) % day;
  const hour = m / 60;
  if (hour >= 17.5 && hour < 19.5) {
    return (hour - 17.5) / 2;
  }
  if (hour >= 19.5 || hour < 5) {
    return 1;
  }
  if (hour >= 5 && hour < 6.5) {
    return 1 - (hour - 5) / 1.5;
  }
  return 0;
}

/** Drift Mount Cinder ash upward. Safe no-op if the plume is missing. */
export function tickPlume(scene: THREE.Scene, time: number): void {
  const plume = scene.getObjectByName('MT-CINDER-PLUME');
  if (!(plume instanceof THREE.Points)) {
    return;
  }
  const attr = plume.geometry.getAttribute('position');
  const base = plume.userData.base as Float32Array | undefined;
  if (!attr || !base) {
    return;
  }
  for (let i = 0; i < attr.count; i++) {
    const bx = base[i * 3] ?? 0;
    const by = base[i * 3 + 1] ?? 0;
    const bz = base[i * 3 + 2] ?? 0;
    const rise = ((by / 30 + time * 0.08) % 1) * 30;
    const spin = time * 0.35 + i * 0.2;
    attr.setXYZ(i, bx + Math.sin(spin) * 0.35, rise, bz + Math.cos(spin * 0.8) * 0.28);
  }
  attr.needsUpdate = true;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(lerp(ar, br, t));
  const g = Math.round(lerp(ag, bg, t));
  const bl = Math.round(lerp(ab, bb, t));
  return (r << 16) | (g << 8) | bl;
}

/** Continuous day→night grade. Call every sim tick. Optional player follow for the shadow frustum. */
export function tickSky(
  scene: THREE.Scene,
  todMinutes: number,
  follow?: { x: number; z: number },
): void {
  const mesh = scene.getObjectByName('SKY-DOME');
  if (!(mesh instanceof THREE.Mesh)) {
    return;
  }
  const t = nightBlend(todMinutes);
  mesh.userData.night = t >= 0.5;
  const nightDome = scene.getObjectByName('SKY-DOME-NIGHT');
  if (nightDome instanceof THREE.Mesh && nightDome.material instanceof THREE.MeshBasicMaterial) {
    nightDome.material.opacity = t;
  }
  const sun = mesh.userData.sun as THREE.DirectionalLight | undefined;
  const hemi = mesh.userData.hemi as THREE.HemisphereLight | undefined;
  const fill = mesh.userData.fill as THREE.DirectionalLight | undefined;
  if (sun) {
    sun.intensity = lerp(1.95, 0.58, t);
    sun.color.setHex(lerpHex(SUN_COLOR, 0x9ab4c8, t));
    sun.castShadow = t < 0.82;
    if (follow) {
      const gx = Math.round(follow.x / 8) * 8;
      const gz = Math.round(follow.z / 8) * 8;
      if (sun.userData.gx !== gx || sun.userData.gz !== gz) {
        sun.userData.gx = gx;
        sun.userData.gz = gz;
        sun.target.position.set(gx, 0, gz);
        sun.position.set(gx - 46, 34, gz + 22);
        sun.target.updateMatrixWorld();
        sun.updateMatrixWorld();
      }
    }
  }
  if (hemi) {
    hemi.intensity = lerp(0.98, 0.5, t);
  }
  if (fill) {
    fill.intensity = lerp(0.48, 0.22, t);
  }
  const fogHex = lerpHex(FOG_COLOR, 0x2a3228, t);
  if (scene.fog instanceof THREE.FogExp2) {
    scene.fog.density = lerp(FOG_DENSITY, 0.007, t);
    scene.fog.color.setHex(fogHex);
  }
  if (scene.background instanceof THREE.Color) {
    scene.background.setHex(fogHex);
  }
}

/** Swap day/night maps and dim the low sun. No-op if the period did not change. */
export function setSkyPeriod(scene: THREE.Scene, night: boolean): void {
  const mesh = scene.getObjectByName('SKY-DOME');
  if (!(mesh instanceof THREE.Mesh)) {
    return;
  }
  if (mesh.userData.night === night) {
    return;
  }
  mesh.userData.night = night;
  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial) {
    const next = (night ? mesh.userData.nightMap : mesh.userData.dayMap) as THREE.Texture | undefined;
    if (next) {
      mat.map = next;
      mat.needsUpdate = true;
    }
  }
  const nightDome = scene.getObjectByName('SKY-DOME-NIGHT');
  if (nightDome instanceof THREE.Mesh && nightDome.material instanceof THREE.MeshBasicMaterial) {
    nightDome.material.opacity = night ? 1 : 0;
  }
  const sun = mesh.userData.sun as THREE.DirectionalLight | undefined;
  const hemi = mesh.userData.hemi as THREE.HemisphereLight | undefined;
  const fill = mesh.userData.fill as THREE.DirectionalLight | undefined;
  if (sun) {
    sun.intensity = night ? 0.58 : 1.95;
    sun.color.setHex(night ? 0x9ab4c8 : SUN_COLOR);
  }
  if (hemi) {
    hemi.intensity = night ? 0.5 : 0.98;
  }
  if (fill) {
    fill.intensity = night ? 0.22 : 0.48;
  }
  if (scene.fog instanceof THREE.FogExp2) {
    scene.fog.density = night ? 0.007 : FOG_DENSITY;
    scene.fog.color.setHex(night ? 0x1c2018 : FOG_COLOR);
  }
  if (scene.background instanceof THREE.Color) {
    scene.background.setHex(night ? 0x1c2018 : FOG_COLOR);
  }
}
